import { addDays, addWeeks, addMonths, addYears, format } from 'date-fns';

/**
 * Single source of truth for recurring frequency math.
 *
 * Canonical custom format: `every-{N}-{days|weeks|months|years}` (1 ≤ N ≤ 99).
 * Named shortcuts `bi-monthly` and `semi-annual` are accepted and resolve to
 * `every-2-months` and `every-6-months` respectively.
 *
 * Stays date-fns based so day-of-month behavior matches the rest of the app.
 */

export type FrequencyUnit = 'days' | 'weeks' | 'months' | 'years';

export interface ParsedFrequency {
  /** Normalized unit. */
  unit: FrequencyUnit;
  /** How many of that unit per cycle. */
  count: number;
  /** Original raw string passed in (lower-cased, trimmed). */
  raw: string;
}

const PRESETS: Record<string, { unit: FrequencyUnit; count: number }> = {
  daily: { unit: 'days', count: 1 },
  weekly: { unit: 'weeks', count: 1 },
  biweekly: { unit: 'weeks', count: 2 },
  'bi-weekly': { unit: 'weeks', count: 2 },
  monthly: { unit: 'months', count: 1 },
  'bi-monthly': { unit: 'months', count: 2 },
  bimonthly: { unit: 'months', count: 2 },
  quarterly: { unit: 'months', count: 3 },
  'semi-annual': { unit: 'months', count: 6 },
  semiannual: { unit: 'months', count: 6 },
  'semi-annually': { unit: 'months', count: 6 },
  yearly: { unit: 'years', count: 1 },
  annually: { unit: 'years', count: 1 },
};

const CUSTOM_RE = /^every-(\d{1,2})-(days|weeks|months|years)$/;

export function parseFrequency(frequency: string | null | undefined): ParsedFrequency | null {
  if (!frequency) return null;
  const raw = String(frequency).trim().toLowerCase();
  if (PRESETS[raw]) return { ...PRESETS[raw], raw };
  const m = raw.match(CUSTOM_RE);
  if (m) {
    const count = parseInt(m[1], 10);
    const unit = m[2] as FrequencyUnit;
    if (count >= 1 && count <= 99) return { count, unit, raw };
  }
  return null;
}

function step(date: Date, unit: FrequencyUnit, count: number): Date {
  switch (unit) {
    case 'days': return addDays(date, count);
    case 'weeks': return addWeeks(date, count);
    case 'months': return addMonths(date, count);
    case 'years': return addYears(date, count);
  }
}

export function advanceByFrequency(date: Date, frequency: string): Date {
  const p = parseFrequency(frequency);
  if (!p) return addMonths(date, 1); // safe fallback
  return step(date, p.unit, p.count);
}

export function regressByFrequency(date: Date, frequency: string): Date {
  const p = parseFrequency(frequency);
  if (!p) return addMonths(date, -1);
  return step(date, p.unit, -p.count);
}

/** Returns next due date as yyyy-MM-dd. */
export function nextDueDateString(fromYmd: string, frequency: string): string {
  const d = new Date(`${fromYmd}T00:00:00`);
  if (isNaN(d.getTime())) return fromYmd;
  return format(advanceByFrequency(d, frequency), 'yyyy-MM-dd');
}

/** Monthly-equivalent amount for budget on-hold math. */
export function monthlyEquivalent(amount: number, frequency: string | null | undefined): number {
  const p = parseFrequency(frequency);
  if (!p) return amount;
  switch (p.unit) {
    case 'days':   return (amount * 30) / p.count;
    case 'weeks':  return (amount * 4.345) / p.count;
    case 'months': return amount / p.count;
    case 'years':  return amount / (p.count * 12);
  }
}

/** Display label, e.g. "Every 2 months", "Weekly", "Semi-annual". */
export function formatFrequencyLabel(frequency: string | null | undefined): string {
  if (!frequency) return '';
  const raw = String(frequency).trim().toLowerCase();
  const named: Record<string, string> = {
    daily: 'Daily',
    weekly: 'Weekly',
    biweekly: 'Bi-weekly',
    'bi-weekly': 'Bi-weekly',
    monthly: 'Monthly',
    'bi-monthly': 'Bi-monthly',
    bimonthly: 'Bi-monthly',
    quarterly: 'Quarterly',
    'semi-annual': 'Semi-annual',
    semiannual: 'Semi-annual',
    'semi-annually': 'Semi-annual',
    yearly: 'Yearly',
    annually: 'Yearly',
  };
  if (named[raw]) return named[raw];
  const p = parseFrequency(raw);
  if (!p) return raw;
  const unitSingular: Record<FrequencyUnit, string> = {
    days: 'day', weeks: 'week', months: 'month', years: 'year',
  };
  return p.count === 1
    ? `Every ${unitSingular[p.unit]}`
    : `Every ${p.count} ${unitSingular[p.unit]}s`;
}

/** Build canonical custom string. */
export function buildCustomFrequency(count: number, unit: FrequencyUnit): string {
  const n = Math.max(1, Math.min(99, Math.floor(count) || 1));
  return `every-${n}-${unit}`;
}

/** True if frequency is a non-preset `every-N-unit` form. */
export function isCustomFrequency(frequency: string | null | undefined): boolean {
  if (!frequency) return false;
  return CUSTOM_RE.test(String(frequency).trim().toLowerCase());
}
